import React, { useState } from 'react';
import { 
  Building2, 
  ChevronRight, 
  Layers, 
  Menu, 
  X, 
  Award, 
  Sparkles,
  MapPin,
  CheckCircle2
} from 'lucide-react';
import { examCatalog, ExamCatalogEntry } from '../data/examCatalog';

interface ExamSidebarProps {
  currentRoute: string;
  onSelectExam: (entry: ExamCatalogEntry) => void;
  onViewAllExams: () => void;
}

export const ExamSidebar: React.FC<ExamSidebarProps> = ({
  currentRoute,
  onSelectExam,
  onViewAllExams
}) => {
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  return (
    <>
      {/* Mobile Toggle Button */}
      <div className="lg:hidden flex items-center justify-between bg-slate-900 text-white px-4 py-2.5 border-b border-slate-800">
        <button
          onClick={() => setIsMobileOpen(!isMobileOpen)}
          className="flex items-center gap-2 text-xs font-bold text-indigo-300 hover:text-white transition"
        >
          {isMobileOpen ? <X className="w-4 h-4 text-rose-400" /> : <Menu className="w-4 h-4 text-indigo-400" />}
          <span>State Exam Directory ({examCatalog.length} States)</span>
        </button>
        <button
          onClick={() => {
            onViewAllExams();
            setIsMobileOpen(false);
          }}
          className="text-[11px] font-semibold text-amber-300 hover:underline"
        >
          View All Exam Types &rarr;
        </button>
      </div>

      {/* Mobile Drawer Overlay */}
      {isMobileOpen && (
        <div 
          className="lg:hidden fixed inset-0 z-40 bg-slate-900/60 backdrop-blur-xs"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Sidebar Navigation */}
      <aside
        className={`
          fixed lg:static inset-y-0 left-0 z-50 w-72 bg-white border-r border-slate-200/90 flex flex-col justify-between transition-transform duration-300 ease-in-out shrink-0
          ${isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        <div className="flex flex-col h-full overflow-y-auto">
          {/* Header */}
          <div className="p-4 border-b border-slate-100 bg-slate-50/80 flex items-center justify-between">
            <div>
              <div className="flex items-center gap-1.5 text-xs font-bold text-indigo-700 uppercase tracking-wider">
                <Layers className="w-4 h-4 text-indigo-600" /> State Certification
              </div>
              <h2 className="text-sm font-extrabold text-slate-800 mt-0.5">
                Exam-Type Directory
              </h2>
            </div>
            <button
              onClick={onViewAllExams}
              className="px-2 py-1 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded text-[11px] font-bold border border-indigo-200 transition"
              title="Overview of all state exam types"
            >
              All States
            </button>
          </div>

          {/* Exam Catalog Item List */}
          <nav className="p-3 space-y-1.5 flex-1">
            <div className="px-2 py-1 text-[10px] font-bold text-slate-400 uppercase tracking-widest">
              Select Your State Exam
            </div>

            {examCatalog.map((entry) => {
              const isActive = currentRoute === entry.route || currentRoute.endsWith(entry.id);
              
              return (
                <a
                  key={entry.id}
                  href={`#${entry.route}`}
                  onClick={(e) => {
                    e.preventDefault();
                    onSelectExam(entry);
                    setIsMobileOpen(false);
                  }}
                  className={`
                    group flex items-start justify-between p-3 rounded-xl transition-all text-left block border ${
                      isActive
                        ? 'bg-indigo-600 text-white border-indigo-700 shadow-xs'
                        : 'bg-white text-slate-800 border-slate-100 hover:border-indigo-200 hover:bg-indigo-50/40'
                    }
                  `}
                >
                  <div className="space-y-0.5 min-w-0 pr-2">
                    <div className="flex items-center gap-1.5">
                      <span className={`font-bold text-xs truncate ${isActive ? 'text-white' : 'text-slate-900 group-hover:text-indigo-700'}`}>
                        {entry.state}
                      </span>
                      {isActive && (
                        <CheckCircle2 className="w-3.5 h-3.5 text-amber-300 shrink-0" />
                      )}
                    </div>
                    <p className={`text-[11px] line-clamp-1 font-medium ${isActive ? 'text-indigo-100' : 'text-slate-500'}`}>
                      {entry.examLabel}
                    </p>
                  </div>

                  <ChevronRight 
                    className={`w-4 h-4 mt-0.5 shrink-0 transition-transform ${
                      isActive ? 'text-white translate-x-0.5' : 'text-slate-300 group-hover:text-indigo-600 group-hover:translate-x-0.5'
                    }`} 
                  />
                </a>
              );
            })}
          </nav>

          {/* Footer Card */}
          <div className="p-4 m-3 bg-slate-50 rounded-xl border border-slate-200/80 space-y-2 text-xs">
            <div className="flex items-center gap-1.5 font-bold text-slate-800">
              <Sparkles className="w-4 h-4 text-amber-500" />
              <span>Need Another State?</span>
            </div>
            <p className="text-[11px] text-slate-500 leading-relaxed">
              If your state uses Praxis Series, select <strong>Most Other States</strong> to view Praxis Core, Subject, and PLT tests.
            </p>
          </div>
        </div>
      </aside>
    </>
  );
};
